import api from "./api";

export const mapProgramId = (prog) => {
  if (!prog) return "OTHER";
  const str = String(prog).toUpperCase().trim();

  if (str.includes("DIABETES")) {
    return "DIABETES";
  }
  if (str.includes("WEIGHT") || str.includes("WEIGHT_MANAGEMENT")) {
    return "WEIGHT_MANAGEMENT";
  }
  if (str.includes("WOMEN") || str.includes("WOMENS_HEALTH")) {
    return "WOMENS_HEALTH_PROGRAM";
  }
  if (str.includes("COPAY") || str.includes("COPAY_ASSIST")) {
    return "COPAY_ASSIST";
  }
  if (str.includes("BIOSIMILAR") || str.includes("BIOSIMILARS")) {
    return "BIOSIMILARS";
  }
  if (str.includes("GENERAL") || str.includes("OTHER")) {
    return "OTHER";
  }

  return "OTHER";
};

export const submitAssessmentApi = async (id, payload) => {
  const response = await api.post(`/api/v1/assessments/${id}/submit`, payload);
  return response.data;
};

export const autosaveAssessmentApi = async (id, payload) => {
  const response = await api.patch(
    `/api/v1/assessments/${id}/autosave`,
    payload,
  );
  return response.data;
};

export const getClinicalNoteHistory = async (memberId) => {
  const response = await api.get(
    `/api/v1/assessments/members/${memberId}/history`,
  );
  return response.data;
};

// --- Template Configuration APIs ---

export const parseSchemaPayload = (schemaPayload) => {
  if (!schemaPayload) return {};
  if (typeof schemaPayload === "string") {
    try {
      return JSON.parse(schemaPayload);
    } catch (e) {
      console.error("Failed to parse schemaPayload JSON string:", e);
      return {};
    }
  }
  return schemaPayload;
};

export const getTemplateSchemaFromPayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const keys = Object.keys(payload);
  if (keys.length === 0) return null;
  const subPayload = payload[keys[0]];
  if (!subPayload || typeof subPayload !== "object") return null;

  if (subPayload.form && subPayload.sections) {
    return subPayload;
  }

  const subKeys = Object.keys(subPayload);
  for (const subKey of subKeys) {
    const val = subPayload[subKey];
    if (val && typeof val === "object" && val.form && val.sections) {
      return val;
    }
  }

  return subPayload;
};

export const cleanProgramId = (id) => {
  if (!id) return "";
  const s = String(id).toUpperCase().trim();
  if (s.includes("DIABETES")) return "DIABETES";
  if (s.includes("WEIGHT")) return "WEIGHT_MANAGEMENT";
  if (s.includes("WOMEN")) return "WOMENS_HEALTH_PROGRAM";
  if (s.includes("COPAY")) return "COPAY_ASSIST";
  if (s.includes("BIOSIMILAR")) return "BIOSIMILARS";
  return s;
};

export const isNonClinicalProgram = (prog, optionalName = "") => {
  if (!prog && !optionalName) return false;
  let id = "";
  let name = "";
  if (typeof prog === "string") {
    id = prog;
    name = optionalName || prog;
  } else {
    id = String(
      prog?.programId ||
        prog?.id ||
        prog?.code ||
        prog?.programCode ||
        prog?.cleanId ||
        "",
    );
    name = String(
      optionalName ||
        prog?.programName ||
        prog?.name ||
        prog?.displayName ||
        prog?.programDisplayName ||
        prog?.groupName ||
        prog?.category ||
        "",
    );
  }
  const upperId = id.toUpperCase().trim();
  const upperName = name.toUpperCase().trim();
  return (
    upperId.includes("BIOSIMILAR") ||
    upperName.includes("BIOSIMILAR") ||
    upperId.includes("COPAY") ||
    upperName.includes("COPAY") ||
    upperId.includes("SCRIPTCO") ||
    upperName.includes("SCRIPTCO") ||
    upperId.includes("OTHER") ||
    upperName.includes("OTHER") ||
    upperId.includes("GENERAL") ||
    upperName.includes("GENERAL") ||
    upperId.includes("LIFESTYLE") ||
    upperName.includes("LIFESTYLE") ||
    upperId.includes("CARE_PLAN") ||
    upperName.includes("CARE_PLAN") ||
    upperId.includes("CAREPLAN") ||
    upperName.includes("CAREPLAN") ||
    upperId.includes("METADATA") ||
    upperName.includes("METADATA")
  );
};

export const normalizeProgramId = (id) => {
  if (!id) return "";
  const upper = String(id).toUpperCase().trim();
  if (upper.includes("WOMEN")) {
    return "WOMENS_HEALTH_PROGRAM";
  }
  return upper
    .replace(/_PROGRAM_SPECIFIC|_PROGRAM/gi, "")
    .replace(/[\s_-]+/g, "")
    .trim();
};

export const isEnrolledOutcome = (val, referenceData = null) => {
  if (!val) return false;
  const norm = String(val).toUpperCase().trim();

  if (referenceData?.outcomes && Array.isArray(referenceData.outcomes)) {
    const matched = referenceData.outcomes.find(
      (o) =>
        o.outcomeName === val ||
        o.displayName === val ||
        o.outcome === val ||
        String(o.outcomeName || "")
          .toUpperCase()
          .trim() === norm ||
        String(o.displayName || "")
          .toUpperCase()
          .trim() === norm,
    );
    if (matched) {
      const mName = String(matched.outcomeName || "")
        .toUpperCase()
        .trim();
      const mDisp = String(matched.displayName || "")
        .toLowerCase()
        .trim();
      return mName === "ENROLLED" || mDisp === "enrolled";
    }
  }

  return (
    norm === "ENROLLED" ||
    norm === "ENROLLED/PROCEEDING" ||
    norm === "ENROLLED / PROCEEDING" ||
    (norm.startsWith("ENROLL") &&
      !norm.includes("NOT") &&
      !norm.includes("PROGRESS"))
  );
};

export const isProgramCompletionOutcome = (val, referenceData = null) => {
  if (!val) return false;
  const norm = String(val).toUpperCase().trim();

  // If referenceData contains outcome
  if (referenceData?.outcomes && Array.isArray(referenceData.outcomes)) {
    const matched = referenceData.outcomes.find(
      (o) =>
        o.outcomeName === val ||
        o.displayName === val ||
        o.outcome === val ||
        String(o.outcomeName || "")
          .toUpperCase()
          .trim() === norm ||
        String(o.displayName || "")
          .toUpperCase()
          .trim() === norm ||
        String(o.outcome || "")
          .toUpperCase()
          .trim() === norm,
    );
    if (matched) {
      const mName = String(matched.outcomeName || matched.outcome || "")
        .toUpperCase()
        .trim();
      const mDisp = String(matched.displayName || "")
        .toUpperCase()
        .trim();
      return (
        mName === "PROGRAM_COMPLETION" ||
        mName === "PROGRAM COMPLETION" ||
        mName === "PROGRAM_COMPLETE" ||
        mName === "PROGRAM COMPLETE" ||
        mName === "PROGRAM_COMPLETED" ||
        mName === "PROGRAM COMPLETED" ||
        mName === "COMPLETE_PROGRAM" ||
        mName === "COMPLETED_PROGRAM" ||
        mDisp === "PROGRAM COMPLETION" ||
        mDisp === "PROGRAM COMPLETE" ||
        mDisp === "PROGRAM COMPLETED" ||
        mDisp === "COMPLETED PROGRAM" ||
        (mName.includes("PROGRAM") && mName.includes("COMPLET")) ||
        (mDisp.includes("PROGRAM") && mDisp.includes("COMPLET"))
      );
    }
  }

  return (
    norm === "PROGRAM_COMPLETION" ||
    norm === "PROGRAM COMPLETION" ||
    norm === "PROGRAM_COMPLETE" ||
    norm === "PROGRAM COMPLETE" ||
    norm === "PROGRAM_COMPLETED" ||
    norm === "PROGRAM COMPLETED" ||
    norm === "COMPLETE_PROGRAM" ||
    norm === "COMPLETED_PROGRAM" ||
    norm.includes("PROGRAM_COMPLETION") ||
    norm.includes("PROGRAM COMPLETION") ||
    norm.includes("PROGRAM_COMPLETED") ||
    norm.includes("PROGRAM COMPLETED") ||
    (norm.includes("PROGRAM") && norm.includes("COMPLET"))
  );
};

export const isDeclinedOutcome = (val, referenceData = null) => {
  if (!val) return false;
  if (isProgramCompletionOutcome(val, referenceData)) return false;
  if (isEnrolledOutcome(val, referenceData)) return false;
  const norm = String(val).toUpperCase().trim();

  // Reference data lookup for Declined
  if (referenceData?.outcomes && Array.isArray(referenceData.outcomes)) {
    const matched = referenceData.outcomes.find(
      (o) =>
        o.outcomeName === val ||
        o.displayName === val ||
        o.outcome === val ||
        String(o.outcomeName || "")
          .toUpperCase()
          .trim() === norm ||
        String(o.displayName || "")
          .toUpperCase()
          .trim() === norm ||
        String(o.outcome || "")
          .toUpperCase()
          .trim() === norm,
    );
    if (matched) {
      const matchName = String(
        matched.outcomeName || matched.outcome || matched.displayName || "",
      ).toUpperCase();
      return matchName.includes("DECLIN") || matchName.includes("REFUS");
    }
  }

  // Fallback
  return norm.includes("DECLIN") || norm.includes("REFUS");
};

export const isTerminalOutcome = (val, referenceData = null) => {
  if (!val) return false;
  if (isEnrolledOutcome(val, referenceData)) return false;
  const norm = String(val).toUpperCase().trim();

  // 1. Member Declined / Refused
  if (
    isDeclinedOutcome(val, referenceData) ||
    norm.includes("DECLIN") ||
    norm.includes("REFUS")
  ) {
    return true;
  }

  // 2. Do Not Call
  if (
    norm.includes("DO NOT CALL") ||
    norm.includes("DO_NOT_CALL") ||
    norm === "DNC"
  ) {
    return true;
  }

  // 3. Not Enrolled
  if (
    norm.includes("NOT ENROLL") ||
    norm.includes("NOT_ENROLL") ||
    norm === "NOT ENROLLED" ||
    norm === "NOT_ENROLLED"
  ) {
    return true;
  }

  // 4. Program Completed / Program Completion
  if (isProgramCompletionOutcome(val, referenceData)) {
    return true;
  }

  // 5. Reference data match for terminal outcomes (Member Declined, Do Not Call, Not Enrolled, Program Completion)
  if (referenceData?.outcomes && Array.isArray(referenceData.outcomes)) {
    const matched = referenceData.outcomes.find(
      (o) =>
        o.outcomeName === val ||
        o.displayName === val ||
        o.outcome === val ||
        String(o.outcomeName || "")
          .toUpperCase()
          .trim() === norm ||
        String(o.displayName || "")
          .toUpperCase()
          .trim() === norm ||
        String(o.outcome || "")
          .toUpperCase()
          .trim() === norm,
    );
    if (matched) {
      const matchName = String(
        matched.outcomeName || matched.outcome || matched.displayName || "",
      ).toUpperCase();
      if (
        matchName.includes("DECLIN") ||
        matchName.includes("REFUS") ||
        matchName.includes("DO NOT CALL") ||
        matchName.includes("DO_NOT_CALL") ||
        matchName.includes("NOT ENROLL") ||
        matchName.includes("NOT_ENROLL") ||
        matchName === "NOT_ENROLLED" ||
        matchName === "NOT ENROLLED" ||
        (matchName.includes("PROGRAM") && matchName.includes("COMPLET"))
      ) {
        return true;
      }
    }
  }

  return false;
};

export const hasTerminalOutcome = (item, referenceData = null) => {
  if (!item) return false;
  if (typeof item === "string") {
    return isTerminalOutcome(item, referenceData);
  }

  const candidateValues = [
    item.outcome,
    item.status,
    item.programOutcome,
    item.outcomeName,
    item.recentActivityOutcome,
    typeof item.recentActivity === "string"
      ? item.recentActivity
      : item.recentActivity?.outcome ||
        item.recentActivity?.status ||
        item.recentActivity?.outcomeName,
    item.lastOutcome,
    item.disposition,
    item.originalObj?.outcome,
    item.originalObj?.status,
    item.originalObj?.programOutcome,
    item.originalObj?.recentActivityOutcome,
    typeof item.originalObj?.recentActivity === "string"
      ? item.originalObj?.recentActivity
      : item.originalObj?.recentActivity?.outcome ||
        item.originalObj?.recentActivity?.status,
  ];

  for (const val of candidateValues) {
    if (val && isTerminalOutcome(val, referenceData)) {
      return true;
    }
  }

  return false;
};

export const isUnableToReachOutcome = (val, referenceData = null) => {
  if (!val) return false;
  if (isEnrolledOutcome(val, referenceData)) return false;
  if (isProgramCompletionOutcome(val, referenceData)) return false;
  if (isDeclinedOutcome(val, referenceData)) return false;
  return true;
};

export const isDeclinedOrUnableToReach = (val, referenceData = null) => {
  if (!val) return false;
  return (
    !isEnrolledOutcome(val, referenceData) &&
    !isProgramCompletionOutcome(val, referenceData)
  );
};

export const getOutcomeNameFromReferenceData = (val, referenceData = null) => {
  if (!val) return "";
  const norm = String(val).toUpperCase().trim();

  if (referenceData?.outcomes && Array.isArray(referenceData.outcomes)) {
    const matched = referenceData.outcomes.find(
      (o) =>
        o.outcomeName === val ||
        o.displayName === val ||
        o.outcome === val ||
        String(o.outcomeName || "")
          .toUpperCase()
          .trim() === norm ||
        String(o.displayName || "")
          .toUpperCase()
          .trim() === norm,
    );
    if (matched?.outcomeName) return matched.outcomeName;
    if (matched?.outcome) return matched.outcome;
  }

  if (
    norm === "ENROLLED" ||
    norm === "ENROLLED/PROCEEDING" ||
    norm === "ENROLLED / PROCEEDING"
  ) {
    return "ENROLLED";
  }
  if (norm === "MEMBER DECLINED" || norm === "MEMBER_DECLINED") {
    return "MEMBER_DECLINED";
  }
  if (norm === "NOT ENROLLED" || norm === "NOT_ENROLLED") {
    return "NOT_ENROLLED";
  }

  return "";
};

const groupCache = new Map();
const groupInFlight = new Map();
const groupsCache = new Map();
const groupsInFlight = new Map();

export const clearAssessmentGroupCache = () => {
  groupCache.clear();
  groupInFlight.clear();
  groupsCache.clear();
  groupsInFlight.clear();
};

export const fetchAssessmentGroups = async (params) => {
  const apiParams = {};
  if (params) {
    if (params.state) apiParams.state = params.state.toUpperCase();
    if (params.programId) {
      apiParams.programId = params.programId.toUpperCase();
      apiParams.groupType =
        params.programId.toUpperCase() === "GLOBAL"
          ? "GENERAL"
          : "PROGRAM_SPECIFIC";
    }
  }
  const cacheKey = JSON.stringify(apiParams);
  if (groupsCache.has(cacheKey)) {
    return groupsCache.get(cacheKey);
  }
  if (groupsInFlight.has(cacheKey)) {
    return groupsInFlight.get(cacheKey);
  }
  const promise = (async () => {
    try {
      const response = await api.get("/api/v1/admin/assessment-groups", {
        params: apiParams,
      });
      groupsCache.set(cacheKey, response.data);
      return response.data;
    } finally {
      groupsInFlight.delete(cacheKey);
    }
  })();
  groupsInFlight.set(cacheKey, promise);
  return promise;
};

// Fetches the latest version of every assessment group, including both active
// and draft templates. The list endpoint filters by a single state, so we fetch
// active and draft separately and merge by group id, preferring the draft
// (newer) version when a group has one.
export const fetchLatestAssessmentGroups = async (params = {}) => {
  const [activeGroups, draftGroups] = await Promise.all([
    fetchAssessmentGroups({ ...params, state: "ACTIVE" }),
    fetchAssessmentGroups({ ...params, state: "DRAFT" }),
  ]);

  const mergedById = new Map();
  (Array.isArray(activeGroups) ? activeGroups : []).forEach((group) => {
    if (group?.id != null) mergedById.set(group.id, group);
  });
  // Drafts overwrite active entries for the same group (draft is the latest).
  (Array.isArray(draftGroups) ? draftGroups : []).forEach((group) => {
    if (group?.id != null) mergedById.set(group.id, group);
  });

  return Array.from(mergedById.values());
};

export const fetchAssessmentGroupById = async (id, state) => {
  if (!id) return null;
  const cacheKey = `${id}_${state ? String(state).toUpperCase() : "DEFAULT"}`;
  if (groupCache.has(cacheKey)) {
    return groupCache.get(cacheKey);
  }
  if (groupInFlight.has(cacheKey)) {
    return groupInFlight.get(cacheKey);
  }
  const promise = (async () => {
    try {
      const params = state ? { state: state.toUpperCase() } : undefined;
      const response = await api.get(`/api/v1/admin/assessment-groups/${id}`, {
        params,
      });
      groupCache.set(cacheKey, response.data);
      return response.data;
    } finally {
      groupInFlight.delete(cacheKey);
    }
  })();
  groupInFlight.set(cacheKey, promise);
  return promise;
};

export const createAssessmentGroup = async (groupPayload, draft = false) => {
  clearAssessmentGroupCache();
  const response = await api.post(
    "/api/v1/admin/assessment-groups",
    groupPayload,
    {
      params: draft ? { draft: true } : undefined,
    },
  );
  return response.data;
};

export const updateAssessmentGroup = async (
  id,
  formType,
  templatePayload,
  expectedVersion,
  draft,
  groupName,
) => {
  clearAssessmentGroupCache();
  const payload = Array.isArray(templatePayload)
    ? templatePayload
    : [templatePayload];
  const params = {};
  if (expectedVersion !== undefined) params.expectedVersion = expectedVersion;
  if (draft) params.draft = true;
  if (groupName !== undefined) params.groupName = groupName;
  const response = await api.put(
    `/api/v1/admin/assessment-groups/${id}/templates/${formType.toUpperCase()}`,
    payload,
    {
      params: Object.keys(params).length > 0 ? params : undefined,
    },
  );
  return response.data;
};

// Saves a template as a draft using a keepalive fetch so the request survives a
// page reload/close (axios/XHR requests are cancelled when the page unloads).
export const saveAssessmentDraftBeacon = (
  id,
  formType,
  templatePayload,
  expectedVersion,
  groupName,
) => {
  clearAssessmentGroupCache();
  const payload = Array.isArray(templatePayload)
    ? templatePayload
    : [templatePayload];
  const params = new URLSearchParams({ draft: "true" });
  if (expectedVersion !== undefined) {
    params.set("expectedVersion", String(expectedVersion));
  }
  if (groupName !== undefined) {
    params.set("groupName", String(groupName));
  }
  const token = localStorage.getItem("token");
  const url = `${process.env.REACT_APP_BASE_URL}/halo/api/v1/admin/assessment-groups/${id}/templates/${formType.toUpperCase()}?${params.toString()}`;
  return fetch(url, {
    method: "PUT",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      "X-Tenant-ID": process.env.REACT_APP_TENANT_ID,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
};

export const deleteAssessmentGroup = async (id) => {
  clearAssessmentGroupCache();
  const response = await api.delete(`/api/v1/admin/assessment-groups/${id}`);
  return response.data;
};

let cachedAssessmentRefData = null;

export const getCachedAssessmentReferenceData = () => cachedAssessmentRefData;

export const setCachedAssessmentReferenceData = (data) => {
  cachedAssessmentRefData = data;
};

export const checkIsFormless = (prog, refData = null) => {
  if (!prog) return false;
  if (isNonClinicalProgram(prog)) return false;
  if (typeof prog === "string") {
    prog = { id: prog, name: prog };
  }
  if (
    prog.formless === true ||
    String(prog.formless).toLowerCase() === "true" ||
    prog.isFormless === true ||
    String(prog.isFormless).toLowerCase() === "true"
  ) {
    return true;
  }

  const clean = (s) =>
    String(s || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");

  const pid = clean(
    prog.programCode ||
      prog.programId ||
      prog.code ||
      prog.id ||
      prog.cleanId ||
      "",
  );
  const pname = clean(
    prog.programName ||
      prog.name ||
      prog.displayName ||
      prog.groupName ||
      prog.programDisplayName ||
      prog.program ||
      prog.category ||
      "",
  );

  const referenceData = refData || cachedAssessmentRefData;
  let refPrograms = [];
  if (Array.isArray(referenceData)) {
    refPrograms = referenceData;
  } else if (Array.isArray(referenceData?.programs)) {
    refPrograms = referenceData.programs;
  } else if (Array.isArray(referenceData?.data)) {
    refPrograms = referenceData.data;
  }

  if (refPrograms.length > 0) {
    const matched = refPrograms.find((rp) => {
      const rpCode = clean(
        rp.programCode || rp.programId || rp.code || rp.id || "",
      );
      const rpName = clean(
        rp.programName ||
          rp.name ||
          rp.displayName ||
          rp.groupName ||
          rp.programDisplayName ||
          rp.program ||
          rp.category ||
          "",
      );
      if (!rpCode && !rpName) return false;

      return (
        (pid && rpCode && pid === rpCode) ||
        (pname && rpName && pname === rpName) ||
        (pid && rpName && pid === rpName) ||
        (pname && rpCode && pname === rpCode) ||
        (pname &&
          rpName &&
          (pname.includes(rpName) || rpName.includes(pname))) ||
        (pid && rpName && (pid.includes(rpName) || rpName.includes(pid))) ||
        (pname &&
          rpCode &&
          (pname.includes(rpCode) || rpCode.includes(pname))) ||
        (pid && rpCode && (pid.includes(rpCode) || rpCode.includes(pid)))
      );
    });
    if (
      matched &&
      (matched.formless === true ||
        String(matched.formless).toLowerCase() === "true" ||
        matched.isFormless === true ||
        String(matched.isFormless).toLowerCase() === "true")
    ) {
      return true;
    }
  }
  return false;
};

export const initializeAssessmentApi = async (payload) => {
  let sanitizedPayload = payload;
  if (payload && Array.isArray(payload.programs)) {
    let refData = cachedAssessmentRefData;
    if (!refData) {
      try {
        refData = await fetchAssessmentReferenceData();
      } catch (e) {
        // ignore fallback
      }
    }

    const sanitizedPrograms = payload.programs.map((p) => {
      const isFormless =
        checkIsFormless(p, refData) ||
        p.formType === "N/A" ||
        p.formless === true ||
        p.isFormless === true;
      if (isFormless) {
        const { formType, manuallyAdded, ...rest } = p;
        return rest;
      }
      const item = { ...p };
      if (!item.manuallyAdded) {
        delete item.manuallyAdded;
      }
      return item;
    });

    sanitizedPayload = {
      ...payload,
      programs: sanitizedPrograms,
    };
  }

  const response = await api.post("/api/v1/assessments", sanitizedPayload);
  return response.data;
};

export const fetchAssessmentSchemasApi = async (programsQueryParams) => {
  const searchParams = new URLSearchParams();
  if (Array.isArray(programsQueryParams)) {
    programsQueryParams.forEach((param) => {
      if (param) searchParams.append("programs", param);
    });
  }
  const response = await api.get(
    `/api/v1/assessments/schemas?${searchParams.toString()}`,
  );
  return response.data;
};

export const parseSchemasResponse = (data) => {
  if (!data)
    return {
      generalSchema: null,
      lifestyleSchema: null,
      carePlanSchema: null,
      programSchemasMap: {},
      warnings: {},
    };

  let generalSchema = null;
  let lifestyleSchema = null;
  let carePlanSchema = null;
  const programSchemasMap = {};
  const warnings = {
    ...data.warnings,
    ...data.schemaWarnings,
    ...data.payload?.warnings,
    ...data.payload?.schemaWarnings,
    ...data.schemas?.warnings,
    ...data.schemas?.schemaWarnings,
  };

  const processSchemaItem = (
    key,
    schemaObj,
    formType = null,
    progId = null,
  ) => {
    if (!schemaObj) return;
    const uKey = String(key || progId || "").toUpperCase();
    if (
      uKey === "WARNINGS" ||
      uKey === "SCHEMAWARNINGS" ||
      uKey === "SCHEMA_WARNINGS"
    ) {
      return;
    }

    const parsed = parseSchemaPayload(schemaObj);
    const resolved = getTemplateSchemaFromPayload(parsed) || parsed;

    if (
      uKey.includes("LIFESTYLE") ||
      String(schemaObj.groupType || "").toUpperCase() === "LIFESTYLE"
    ) {
      if (!lifestyleSchema) lifestyleSchema = resolved;
    } else if (
      uKey.includes("CARE_PLAN") ||
      uKey.includes("CAREPLAN") ||
      String(schemaObj.groupType || "").toUpperCase() === "CARE_PLAN"
    ) {
      if (!carePlanSchema) carePlanSchema = resolved;
    } else if (
      uKey.includes("GENERAL") ||
      uKey.includes("OTHER") ||
      uKey === "GLOBAL" ||
      String(schemaObj.groupType || "").toUpperCase() === "GENERAL"
    ) {
      if (!generalSchema) generalSchema = resolved;
    } else {
      const cleanId = cleanProgramId(progId || key);
      if (cleanId) {
        programSchemasMap[`${cleanId}_PROGRAM_SPECIFIC`] = resolved;
        programSchemasMap[cleanId] = resolved;
        programSchemasMap[key] = resolved;
      }
    }
  };

  // Case 1: data has templates array
  const templates = data.templates || (Array.isArray(data) ? data : null);
  if (Array.isArray(templates)) {
    templates.forEach((t) => {
      const prog = t.programId || t.program || t.groupName || t.id;
      processSchemaItem(prog, t.schemaPayload || t, t.formType, prog);
    });
  }

  // Case 2: data is an object with schema keys (e.g. { OTHER_GENERAL_INITIAL: {...}, DIABETES_PROGRAM_SPECIFIC: {...} })
  if (typeof data === "object" && !Array.isArray(data)) {
    const rawPayload = data.payload || data.schemas || data;
    Object.keys(rawPayload).forEach((k) => {
      if (
        k === "warnings" ||
        k === "schemaWarnings" ||
        k === "schema_warnings" ||
        k === "templates" ||
        k === "programs" ||
        k === "id" ||
        k === "version" ||
        k === "assessmentType"
      )
        return;
      processSchemaItem(k, rawPayload[k]);
    });
  }

  if (
    warnings.LIFESTYLE_BARRIERS_FUNCTIONAL_ASSESSMENT ||
    warnings.LIFESTYLE ||
    warnings.lifestyle
  ) {
    lifestyleSchema = null;
  }
  if (
    warnings.CARE_PLAN_COORDINATION_COMPLETION ||
    warnings.CARE_PLAN ||
    warnings.care_plan
  ) {
    carePlanSchema = null;
  }
  if (
    warnings.OTHER_GENERAL ||
    warnings.GENERAL ||
    warnings.general ||
    warnings.OTHER ||
    warnings.other
  ) {
    generalSchema = null;
  }

  return {
    generalSchema,
    lifestyleSchema,
    carePlanSchema,
    programSchemasMap,
    warnings,
  };
};

export const fetchDraftPayloadApi = async (assessmentId, params = {}) => {
  const queryParams = { ...params };
  const response = await api.get(`/api/v1/assessments/${assessmentId}`, {
    params: queryParams,
  });
  return response.data;
};

export const validateAssessmentGroupSchema = async (payload) => {
  try {
    const response = await api.post(
      "/api/v1/admin/assessment-groups/validate",
      payload,
    );
    return { valid: true, data: response.data };
  } catch (e) {
    return {
      valid: false,
      message: e.response?.data?.message || "Invalid schema configuration",
    };
  }
};

export const fetchAssessmentTemplateByType = async (type) => {
  const normType = type.toUpperCase();

  if (normType === "FOLLOW_UP") {
    return {
      formType: "FOLLOW_UP",
      form: {
        title: "",
        description:
          "Progress monitoring and care plan adjustment for established members",
      },
      sections: {},
      schemaPayload: null,
    };
  }

  if (normType === "DISCHARGE") {
    return {
      formType: "DISCHARGE",
      form: {
        title: "",
        description: "Final assessment and transition of care documentation",
      },
      sections: {},
      schemaPayload: null,
    };
  }

  return {
    formType: "INITIAL",
    form: {
      title: "",
      description:
        "Structured RN assessment for symptom burden, medication support, risk identification, and care coordination",
    },
    sections: {},
    schemaPayload: null,
  };
};

export const fetchAssessmentReferenceData = async (params) => {
  if (!params && cachedAssessmentRefData) {
    return cachedAssessmentRefData;
  }
  const response = await api.get("/api/assessment-reference-data", { params });
  if (response.data) {
    cachedAssessmentRefData = response.data;
  }
  return response.data;
};
